import PropTypes from "prop-types";
import { useMemo } from "react";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { DialogClose } from "@/components/ui/dialog";
import { Empty, EmptyDescription } from "@/components/ui/empty";
import {
  BaseForm,
  FormChipList,
  FormDatePicker,
  FormInput,
  FormMultiSelect,
  FormSelect,
  FormToggle,
} from "components/BaseForm";
import tagConstants from "constants/Tag";
import {
  initFields,
  isFieldLocked,
  isMultiValued,
  metadataFieldKey,
  toFormValue,
  toMetadataValue,
} from "helpers/tagMetadata";

const { metadataTypes } = tagConstants;

const MAX_VALUE_LENGTH = 2048;
// Up to two decimals, as the number input has always allowed.
const NUMBER_PATTERN = /^[+-]?(\d+(\.\d{0,2})?|\.\d{1,2})$/;

const fieldSchema = field => {
  const requiredMessage = `${field.field_name} is required`;
  const isBlank = value => value == null || value === "";

  if (field.field_type === metadataTypes.boolean) return z.boolean();
  if (isMultiValued(field)) {
    const items = z.array(z.string());
    return field.required ? items.min(1, requiredMessage) : items;
  }

  let schema = z.string().max(MAX_VALUE_LENGTH).nullable();
  if (field.field_type === metadataTypes.number) {
    schema = schema.refine(
      value => isBlank(value) || NUMBER_PATTERN.test(value),
      "Enter a number with up to 2 decimal places"
    );
  }
  return field.required
    ? schema.refine(value => !isBlank(value), requiredMessage)
    : schema;
};

const selectOptions = field =>
  (field.field_options?.options ?? []).map(option => ({
    key: option,
    value: option,
    text: option,
  }));

const MetadataControl = ({ field }) => {
  const controlProps = {
    name: metadataFieldKey(field),
    id: `tag-metadata-${field.id}`,
    label: field.field_name,
    required: field.required,
    disabled: isFieldLocked(field),
  };
  switch (field.field_type) {
    case metadataTypes.number:
      return <FormInput {...controlProps} type="number" step={0.01} />;
    case metadataTypes.boolean:
      return <FormToggle {...controlProps} />;
    case metadataTypes.date:
      return <FormDatePicker {...controlProps} />;
    case metadataTypes.list:
      return <FormChipList {...controlProps} placeholder="Add item…" />;
    case metadataTypes.categorical:
      return isMultiValued(field) ? (
        <FormMultiSelect {...controlProps} options={selectOptions(field)} />
      ) : (
        <FormSelect
          {...controlProps}
          options={selectOptions(field)}
          clearable
        />
      );
    default:
      return <FormInput {...controlProps} />;
  }
};

/**
 * The metadata fields of one tag, limited to the fields the current role can
 * read or edit. A field without Allow Override, or without edit access, shows
 * its current value as a disabled control.
 *
 * `onSubmit` receives the fields with their entered `value`, the shape the tag
 * endpoints take. With nothing to edit there is no submit control and the
 * dismiss action reads "OK".
 *
 * Meant to be rendered inside a `Dialog`: Cancel and OK are a `DialogClose`,
 * and `onSuccess` runs after `onSubmit` resolves so the dialog's owner can
 * close it.
 */
const ApplyTagMetadataForm = ({ tag, onSubmit, onSuccess }) => {
  const fields = useMemo(() => initFields(tag), [tag]);
  const schema = useMemo(
    () =>
      z.object(
        Object.fromEntries(
          fields
            .filter(field => !isFieldLocked(field))
            .map(field => [metadataFieldKey(field), fieldSchema(field)])
        )
      ),
    [fields]
  );
  const defaultValues = Object.fromEntries(
    fields.map(field => [metadataFieldKey(field), toFormValue(field)])
  );

  const hasFields = fields.length > 0;

  const handleSubmit = values =>
    onSubmit(
      fields.map(field => ({
        ...field,
        value: isFieldLocked(field)
          ? field.value
          : toMetadataValue(field, values[metadataFieldKey(field)]),
      }))
    );

  return (
    <BaseForm
      schema={schema}
      defaultValues={defaultValues}
      onSubmit={hasFields ? handleSubmit : undefined}
      // BaseForm renders a submit control whenever onSuccess is set.
      onSuccess={hasFields ? onSuccess : undefined}
      extraButtons={[
        <DialogClose
          key="dismiss"
          render={<Button type="button" variant="ghost" />}
        >
          {hasFields ? "Cancel" : "OK"}
        </DialogClose>,
      ]}
    >
      {hasFields ? (
        // Only the fields scroll, so the dialog title and the actions stay in
        // view for a tag with many fields. The padding keeps focus rings from
        // being clipped by the scroll container. The global dark scrollbar
        // thumb matches the dialog surface, so it is overridden here; `!` is
        // needed because dark-mode.css is unlayered.
        <div className="tw:-m-1 tw:flex tw:max-h-[60vh] tw:flex-col tw:gap-5 tw:overflow-y-auto tw:p-1 tw:dark:[&::-webkit-scrollbar-thumb]:bg-(--color-text-muted)! tw:dark:[&::-webkit-scrollbar-track]:bg-transparent!">
          {fields.map(field => (
            <MetadataControl key={field.id} field={field} />
          ))}
        </div>
      ) : (
        <Empty>
          <EmptyDescription>
            There are no metadata fields for this tag or you do not have
            required permissions to edit them.
          </EmptyDescription>
        </Empty>
      )}
    </BaseForm>
  );
};

ApplyTagMetadataForm.propTypes = {
  tag: PropTypes.shape({
    id: PropTypes.number,
    tag_metadata_fields: PropTypes.array.isRequired,
    tag_metadata_values: PropTypes.array,
  }).isRequired,
  onSubmit: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

export default ApplyTagMetadataForm;
